import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";
import type { ThaiVisaType } from "@/content/visa-corpus/thai-visa-types";

type VisaCard = Pick<ThaiVisaType, "slug" | "titleEn" | "codeEn" | "whoEn">;

// Registry loads inside the loader (its own lazy chunk) so the 37 KB visa
// corpus never ships in the shared client entry bundle.
export const Route = createFileRoute("/en/visa/thai/")({
  loader: async (): Promise<{ types: VisaCard[] }> => {
    const { THAI_VISA_TYPES } = await import("@/content/visa-corpus/thai-visa-types");
    return {
      types: THAI_VISA_TYPES.map((v) => ({
        slug: v.slug,
        titleEn: v.titleEn,
        codeEn: v.codeEn,
        whoEn: v.whoEn,
      })),
    };
  },
  component: ThaiVisaHubEN,
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const title = "Thailand Visa Types 2026 — Requirements, Documents & Extensions";
    const description =
      "All 12 Thai visa categories compared: Tourist TR/METV, visa exemption, Non-B, Non-ED, Non-O marriage/retirement, O-A, O-X, DTV, LTR and SMART — eligibility, visa validity vs permitted stay, and TM.7 extensions, referenced to Thai government sources.";
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/en/visa/thai",
        type: "website",
        alternates: [{ lang: "th", path: "/visa/thai" }],
        keywords: ["Thailand visa types 2026", "Non-B visa", "DTV visa", "LTR visa", "retirement visa Thailand", "TM.7 extension", "90 day report"],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Thai Visa Types", url: `${site}/en/visa/thai` },
            ]),
          ),
        },
        ...(loaderData
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "ItemList",
                  name: "Thailand visa categories",
                  itemListElement: loaderData.types.map((v, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    name: v.titleEn,
                    url: `${site}/en/visa/thai/${v.slug}`,
                  })),
                }),
              },
            ]
          : []),
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/visa/thai")) },
      ],
    };
  },
});

function ThaiVisaHubEN() {
  const { types } = Route.useLoaderData() as { types: VisaCard[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Thai Visa Types</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">Thailand Visa Types 2026</h1>
        <p className="mb-8 max-w-3xl text-muted-foreground">
          Pick the category that matches your purpose of stay. Each page sets out who qualifies, the documents required, how to file through the
          MFA e-Visa system, how to extend with form TM.7, and the mistakes that most often lead to refusal. Note that visa validity and your
          permitted length of stay are two different things, and every approval is at the discretion of the deciding officer.
        </p>
        <p className="mb-8 text-sm text-muted-foreground">
          <a href="/visa/thai" className="text-primary underline">ภาษาไทย →</a>
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          {types.map((v) => (
            <a key={v.slug} href={`/en/visa/thai/${v.slug}`} className="rounded-lg border bg-card p-5 transition-colors hover:border-primary">
              <h2 className="mb-1 text-lg font-semibold">{v.titleEn}</h2>
              <p className="mb-2 text-xs text-muted-foreground">{v.codeEn}</p>
              <p className="line-clamp-3 text-sm text-muted-foreground">{v.whoEn}</p>
            </a>
          ))}
        </div>

        <section className="mt-10 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">Not sure which visa fits your situation?</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Ask our team by phone, LINE or email, Mon–Sat 9:00–18:00 ICT. We assess your case and quote the service fee before you commit.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">Contact us →</a>
        </section>

        <p className="mt-8 text-sm text-muted-foreground">
          See all questions: <a href="/en/visa/faq" className="text-primary underline">Thai visa &amp; immigration FAQ</a>
        </p>

        <FaqSourceNote topic="visa" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
